import { Add, Event, FilterList, Work } from "@mui/icons-material";
import { Badge, Box, Button, Card, Checkbox, IconButton, List, Popover, Tooltip } from "@mui/material";
import { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  deselectExportType,
  selectDeselectedExportTypes,
  selectExportType,
  selectFilteredCaseExports,
  selectUniqueExportTypes,
} from "redux/slices/case";
import { selectChronologyConfig } from "redux/slices/clientLens";
import { P } from "theme/typography";
import CaseExportItem from "./CaseExportItem";

interface ExportsCenterProps {
  caseId: number;
  setExportCaseProfileModalOpen: (val: boolean) => void;
  setExportCaseChronologyModalOpen: (val: boolean) => void;
}

const ExportsCenter: FC<ExportsCenterProps> = ({
  caseId,
  setExportCaseProfileModalOpen,
  setExportCaseChronologyModalOpen,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const uniqueExportTypes = useAppSelector(selectUniqueExportTypes);
  const filteredExports = useAppSelector(selectFilteredCaseExports);
  const deselectedExportTypes = useAppSelector(selectDeselectedExportTypes);
  const visibleExportTypes = filteredExports.map((e) => e.exportType);
  const chronologyConfig = useAppSelector(selectChronologyConfig);
  const dispatch = useAppDispatch();

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <P variant="h6" data-pendo="export-center-title">
          Exports {filteredExports.length ? `(${filteredExports.length})` : ""}
        </P>
        <Box>
          <Tooltip
            arrow
            title={`${deselectedExportTypes.length} ${
              deselectedExportTypes.length === 1 ? "export" : "exports"
            } filtered`}>
            <IconButton data-pendo="export-filter" onClick={(evt) => setFilterAnchorEl(evt.currentTarget)} size="large">
              <Badge badgeContent={deselectedExportTypes.length} color="primary">
                <FilterList />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Create Export" arrow>
            <IconButton
              data-pendo="case-home-generate-export"
              onClick={(evt) => setAnchorEl(evt.currentTarget)}
              size="large">
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
        <Popover
          open={!!filterAnchorEl}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}>
          <List sx={{ padding: 2 }}>
            {uniqueExportTypes.map((exportType) => (
              <Box key={exportType} sx={{ display: "flex" }}>
                <Checkbox
                  checked={visibleExportTypes.indexOf(exportType) > -1}
                  onChange={(_evt, checked) => {
                    if (!checked) {
                      dispatch(deselectExportType(exportType));
                    } else {
                      dispatch(selectExportType(exportType));
                    }
                  }}
                />
                <P sx={{ paddingTop: "12px" }}>{exportType}</P>
              </Box>
            ))}
          </List>
        </Popover>

        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}>
          <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ padding: 1 }}>
            <Tooltip title="PDF of Case Profile and pinned references" arrow placement="left">
              <Button
                fullWidth
                id="case_form_export"
                color="default"
                size="large"
                onClick={() => {
                  setAnchorEl(null);
                  setExportCaseProfileModalOpen(true);
                }}
                startIcon={<Work />}>
                Case Profile Export
              </Button>
            </Tooltip>
            {chronologyConfig && (
              <Tooltip title="Excel of Case Profile in chronological order" arrow placement="left">
                <Button
                  fullWidth
                  id="case_chronology_export"
                  color="default"
                  size="large"
                  onClick={() => {
                    setAnchorEl(null);
                    setExportCaseChronologyModalOpen(true);
                  }}
                  startIcon={<Event />}>
                  Chronology Export
                </Button>
              </Tooltip>
            )}
          </Box>
        </Popover>
      </Box>

      <Card sx={{ px: 2, py: 1, overflow: "auto", width: "100%", minHeight: filteredExports.length ? 200 : 100 }}>
        {filteredExports.length ? (
          filteredExports.map((exp) => (
            <CaseExportItem
              key={exp.id}
              exp={exp}
              caseId={caseId}
              isLastItem={filteredExports.indexOf(exp) === filteredExports.length - 1}
            />
          ))
        ) : (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <P>No Exports Yet</P>
          </Box>
        )}
      </Card>
    </>
  );
};

export default ExportsCenter;
